/**
 * @file    EnterpriseV2MockDataBuilder.ts
 * @scope   SHARED
 * @summary Builds mock render data for the Enterprise V2 preview from the in-memory selection,
 *          so the widget can be previewed while editing before any real query runs.
 * @consumes EnterpriseGlobalModel, SelectedFieldModel/FieldFilter, the selected trend
 * @produces `EnterpriseV2MockDataBuilder` (build entry point) + period-label helpers
 * @sideEffects None — pure transformation of the current selection into preview rows/columns.
 * @notes Column labels are date-based and rolled back from today by TimePeriodType; see
 *        `PERIOD_TYPE_MAP` for the normalisation of period aliases (monthly/bimonthly → month...).
 */
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { SelectedFieldModel, FieldFilter } from 'models/Enterprise/FieldsManagerModel';

/** Maps normalised TimePeriodType values to their corresponding unit. */
const PERIOD_TYPE_MAP: Record<string, string> = {
  month: 'month',
  monthly: 'month',
  bimonthly: 'month',
  week: 'week',
  weekly: 'week',
  quarter: 'quarter',
  quarterly: 'quarter',
  year: 'year',
  yearly: 'year',
  day: 'day',
  daily: 'day',
};

/** Generates a date-based column label rolling back `offset` periods from today. */
const buildPeriodLabel = (index: number, count: number, timePeriodType?: string): string => {
  const now = new Date();
  const offset = count - 1 - index; // 0 = most recent, (count-1) = oldest
  const normalised = (timePeriodType ?? '').toLowerCase();
  const unit = PERIOD_TYPE_MAP[normalised] ?? 'unknown';

  switch (unit) {
    case 'month':
      return new Date(now.getFullYear(), now.getMonth() - offset, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: '2-digit',
      });

    case 'week': {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay() - offset * 7);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }

    case 'quarter': {
      const d = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - offset * 3, 1);
      return `Q${Math.floor(d.getMonth() / 3) + 1} '${String(d.getFullYear()).slice(-2)}`;
    }

    case 'year':
      return String(now.getFullYear() - offset);

    case 'day': {
      const d = new Date(now);
      d.setDate(d.getDate() - offset);
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    }

    default:
      return `${timePeriodType ?? 'Period'} ${index + 1}`;
  }
};

interface MockColumn {
  displayName: string;
  field: string;
  isNumeric: boolean;
  isDate: boolean;
  isLink: boolean;
  link: string;
  idLink: string;
  isAudioLink: boolean;
  rootURL: null;
  isPercentage: boolean;
  colType: string;
  colName: string;
}

interface MockCellValue {
  value: string;
  isPercentage: boolean;
  significanceIndicator: boolean;
  valueDifference: number;
}

interface MockRow {
  id: string;
  [key: string]: string | number | MockCellValue;
}

interface TrendEntry {
  selectedValue?: number;
  timePeriodType?: string;
}

export interface EnterpriseV2MockResponse {
  data: {
    page: number;
    per_page: number;
    total: number;
    total_page: number;
    data: MockRow[];
    columns: MockColumn[];
    showTotals: boolean;
    baseSize: string;
    controlData: {
      defaultValueL1: { label: string; value: string };
      defaultValueL2: { label: string; value: string };
    };
  };
}

/** Returns a zero value string formatted to the field's decimal precision, e.g. "0", "0.0", "0.00". */
const zeroValue = (field: SelectedFieldModel): string => {
  const parsed = parseInt(field.precision ?? '0', 10);
  const decimals = isNaN(parsed) ? 0 : parsed;
  return decimals > 0 ? `0.${'0'.repeat(decimals)}` : '0';
};

/** Returns true when the field's calculation type is a percentage (Type '2'). */
const isPercentageField = (field: SelectedFieldModel): boolean =>
  String(field.calculationType?.type) === '2';

/** Builds the field_0 order column used for preserving row order (matches real data structure).
 *  Safe to use colType 'Hidden' here because sortingType is forced to NONE for mock data,
 *  so no sorting is triggered and the omit flag only hides the column visually. */
const buildOrderColumn = (): MockColumn => ({
  displayName: 'Order',
  field: 'field_0',
  isNumeric: true,
  isDate: false,
  isLink: false,
  link: '-1',
  idLink: '-1',
  isAudioLink: false,
  rootURL: null,
  isPercentage: false,
  colType: 'Hidden',
  colName: 'Order',
});

const buildFieldNameColumn = (): MockColumn => ({
  displayName: 'Metric',
  field: 'field_1',
  isNumeric: false,
  isDate: false,
  isLink: false,
  link: '-1',
  idLink: '-1',
  isAudioLink: false,
  rootURL: null,
  isPercentage: false,
  colType: '',
  colName: 'Metric',
});

const buildPeriodColumn = (
  index: number,
  count: number,
  timePeriodType: string | undefined
): MockColumn => ({
  displayName: buildPeriodLabel(index, count, timePeriodType),
  field: `field_${index + 2}`,
  isNumeric: true,
  isDate: false,
  isLink: false,
  link: '-1',
  idLink: '-1',
  isAudioLink: false,
  rootURL: null,
  isPercentage: false,
  colType: '',
  colName: `Metric${index + 2}`,
});

/** Returns row type based on IsL1Category toggle. */
const getRowType = (field: SelectedFieldModel): string =>
  field.isL1Category === true ? 'L1' : 'L2';

/** Builds a single empty placeholder row shown when no fields are configured. */
const buildEmptyRow = (trendCount: number): MockRow => {
  const row: MockRow = {
    id: 'enterprise-mock-row-empty',
    field_0: 0,
    field_1: '-',
    rowType: 'L2',
  };
  for (let i = 0; i < trendCount; i++) {
    row[`field_${i + 2}`] = {
      value: '-',
      isPercentage: false,
      significanceIndicator: false,
      valueDifference: 0,
    };
  }
  return row;
};

/** Builds a human-readable tooltip summarising the filters applied to a field. */
const buildFiltersTooltip = (filters: FieldFilter[], logicalOperator?: string): string => {
  if (!filters || filters.length === 0) return '';
  const joiner = `\n`;
  return filters
    .map((f) => {
      const name = f.filterName || 'Unknown';
      const op = f.operator || '—';
      const vals =
        f.selectedValues.length > 0 ? f.selectedValues.map((v) => v.label).join(', ') : 'no values';
      return `${name} ${op} ${vals}`;
    })
    .join(joiner);
};

const buildRow = (
  field: SelectedFieldModel,
  rowIndex: number,
  trendCount: number,
  filters: FieldFilter[]
): MockRow => {
  const row: MockRow = {
    id: `enterprise-mock-row-${rowIndex}`,
    field_0: rowIndex,
    field_1: field.displayName || field.field?.fieldName || '-',
    rowType: getRowType(field),
    ...(filters.length > 0 ? { hasFilters: 1, filterTooltip: buildFiltersTooltip(filters) } : {}),
  };
  const value = zeroValue(field);
  const percentage = isPercentageField(field);
  const hasStat = field.statTesting === true;

  for (let i = 0; i < trendCount; i++) {
    row[`field_${i + 2}`] = {
      value,
      isPercentage: percentage,
      significanceIndicator: hasStat,
      valueDifference: 0,
    };
  }
  return row;
};

/**
 * Builds a DynamicTable-compatible mock response for isDiyWidgetEngine widgets.
 *
 * - Columns: one "Field" label column + N period columns (N = Trend[0].selectedValue).
 * - Rows: one per configured field from enterpriseGlobalObj.
 *
 * Returns null when no fields have been configured yet.
 */
export class EnterpriseV2MockDataBuilder {
  static build(
    enterpriseGlobalObj: EnterpriseGlobalModel,
    trend?: TrendEntry[]
  ): EnterpriseV2MockResponse {
    const selectedEnterpriseFields =
      enterpriseGlobalObj.selectedValues.selectedEnterpriseFields ?? [];

    const trendEntry = trend?.[0];
    const trendCount: number = trendEntry?.selectedValue ?? 1;
    const timePeriodType: string | undefined = trendEntry?.timePeriodType;

    const columns: MockColumn[] = [
      buildOrderColumn(),
      buildFieldNameColumn(),
      ...Array.from({ length: trendCount }, (_, i) =>
        buildPeriodColumn(i, trendCount, timePeriodType)
      ),
    ];

    // Partition-level filters apply to every field of their partition, so they are
    // prepended to each field's own filters as if they were just more filters.
    const rows: MockRow[] = selectedEnterpriseFields
      .flatMap((uf) =>
        (uf.fields ?? []).map((field) => ({
          field,
          filters: [...(uf.partitionFilters ?? []), ...(field.filters ?? [])],
        }))
      )
      .map(({ field, filters }, index) => buildRow(field, index, trendCount, filters));

    // When no fields are configured yet, show a single empty placeholder row
    // that will be replaced automatically once real fields are selected.
    const data: MockRow[] = rows.length > 0 ? rows : [buildEmptyRow(trendCount)];

    return {
      data: {
        page: 1,
        per_page: 100,
        total: data.length,
        total_page: 1,
        data,
        columns,
        showTotals: false,
        baseSize: '',
        controlData: {
          defaultValueL1: { label: '', value: '' },
          defaultValueL2: { label: '', value: '' },
        },
      },
    };
  }
}
